import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectAllEntityLocations } from '@user/store/entityLocations'
import { ILeadDetail } from '@user/store/lead/typings'
import { IOpportunityDetail } from '@user/store/opportunity/typings'
import { sortLocationOrContactByName } from '../ContactsPage/functions'
import { ISecondaryContactDTO, LocationDTO } from '@sales-center/common'

type Location = {
  type: 'location'
  data: Required<
    Omit<
      LocationDTO,
      | 'isSchedulable'
      | 'propertyDetails'
      | 'primaryServiceLocation'
      | 'entityId'
      | 'entityType'
      | 'contactLocationId'
    >
  >
  isBillToAndService: boolean
}

type Contact = {
  type: 'contact'
  data: ISecondaryContactDTO
}

export type LocationOrContact = Location | Contact

export const useCleanupEntityContacts = (
  detail?: ILeadDetail | IOpportunityDetail,
) => {
  const locations = useSelector(selectAllEntityLocations)

  const formattedLocations = useMemo(() => {
    const { billToLocations, serviceLocations } = locations

    const allLocations = [...billToLocations, ...serviceLocations]

    return allLocations.reduce<Location[]>((acc, curr) => {
      const { contact } = curr

      if (!!contact) {
        const idx = acc.findIndex((x) => x.data.contact.id === contact.id)

        if (idx === -1) {
          // no dup, insert it
          const loc: Location = {
            type: 'location',
            data: {
              ...curr,
              contact: contact,
            } as Location['data'],
            isBillToAndService: false,
          }

          return [...acc, loc]
        } else {
          // dup, update existing
          acc[idx].isBillToAndService = true
        }
      }

      return acc
    }, [])
  }, [locations])

  return useMemo(() => {
    if (!detail) return { extContacts: [], wwmContacts: [] }

    const {
      secondaryContactsSummary: { externalContacts, wwmContacts },
    } = detail

    const extContactsTemp: LocationOrContact[] = externalContacts.map(
      (contact) => ({ type: 'contact', data: { contact } }),
    )

    const wwmContactsTemp: LocationOrContact[] = wwmContacts.map((data) => ({
      type: 'contact',
      data,
    }))

    formattedLocations.map((entry) => {
      if (entry.data.externalLinkId) extContactsTemp.push(entry)
      else wwmContactsTemp.push(entry)
    })

    extContactsTemp.sort(sortLocationOrContactByName)
    wwmContactsTemp.sort(sortLocationOrContactByName)

    const { id, externalLinkId } = detail.primaryContact

    const primary = externalLinkId
      ? extContactsTemp.find((x) => x.data.contact.id === id)
      : wwmContactsTemp.find((x) => x.data.contact.id === id)

    return {
      primary,
      extContacts: extContactsTemp,
      wwmContacts: wwmContactsTemp,
    }
  }, [formattedLocations, detail])
}
